import {
    AnyValue,
    Property,
    isIntegerValue,
    isStringValue,
    isTextValue,
    isFloatValue,
    generated,
    isBooleanValue,
    isEnumValue,
    isEmailValue,
    isRelationOneValue,
    isRelationManyValue,
    isDateValue,
    isDateTimeValue,
    isRatingValue,
    isUserValue,
    isLinkValue,
    isSnippetValue,
} from '@varos/research-sdk-api';
import { assert, assertNever, keyBy } from '@varos/util-typescript';
import { ReportingControllerBaseConfig } from '../../../../base';
import { ValueFieldController } from './valueFieldInterface';
import { ValueFieldControllerProps, ValueFieldViewProps } from './valueFieldProps';
import { useEffect, useMemo } from 'react';
import { AnyElementContainerProps } from './element';
import { DistributiveOmit } from '@emotion/react';

export function createValueFieldController(
    config: ReportingControllerBaseConfig
): ValueFieldController {
    function getElementContainerProps(
        props: ValueFieldControllerProps,
        property: Property & {
            optionByKey: Record<string, generated.EnumOption | undefined> | null;
        },
        value: AnyValue | null
    ): DistributiveOmit<AnyElementContainerProps, 'as'> {
        if (property.type === 'string' || property.type === 'text') {
            assert(
                isStringValue(value) || isTextValue(value) || value === null,
                'Value must be a string'
            );
            return {
                kind: 'text',
                value: value,
                format: null,
                onChange(value) {
                    props.value.onChange(value);
                },
            };
        }
        if (property.type === 'email') {
            assert(isEmailValue(value) || value === null, 'Value must be an email');
            return {
                kind: 'text',
                value: value,
                format: {
                    kind: 'email',
                },
                onChange(value) {
                    props.value.onChange(value);
                },
            };
        }
        if (property.type === 'integer' || property.type === 'float') {
            assert(
                isIntegerValue(value) || isFloatValue(value) || value === null,
                'Value must be a number'
            );
            return {
                kind: 'number',
                value: value,
                onChange(value) {
                    props.value.onChange(value);
                },
            };
        }
        if (property.type === 'boolean') {
            assert(isBooleanValue(value) || value === null, 'Value must be a boolean');
            return {
                kind: 'boolean',
                value: value,
                onChange(value) {
                    props.value.onChange(value);
                },
            };
        }
        if (property.type === 'enum') {
            assert(isEnumValue(value) || value === null, 'Value must be an enum');
            return {
                kind: 'enum',
                value: value,
                onChange(value) {
                    props.value.onChange(value);
                },
                item: {
                    property,
                },
            };
        }
        if (
            property.type === 'relation' &&
            (isRelationOneValue(value) || value === null)
        ) {
            return {
                kind: 'text',
                value: value,
                format: null,
                onChange(value) {
                    console.log('relation not implemented', value);
                },
            };
        }
        if (property.type === 'relation') {
            assert(
                isRelationOneValue(value) || isRelationManyValue(value) || value === null,
                'Value must be a relation'
            );
            if (value === null) {
                return {
                    kind: 'text',
                    value: '',
                    format: null,
                    onChange(value) {
                        console.log('relation not implemented', value);
                    },
                };
            }
            if (isRelationOneValue(value)) {
                return {
                    kind: 'text',
                    value: value,
                    format: null,
                    onChange(value) {
                        console.log('relation not implemented', value);
                    },
                };
            }
            if (isRelationManyValue(value)) {
                return {
                    kind: 'text',
                    value: value?.map((item) => item).join(', ') ?? '',
                    format: null,
                    onChange(value) {
                        console.log('relation not implemented', value);
                    },
                };
            }
            assertNever(value);
        }
        if (property.type === 'date') {
            assert(isDateValue(value) || value === null, 'Value must be a date');
            return {
                kind: 'text',
                value: value,
                format: null,
                onChange(value) {
                    console.log('date not implemented', value);
                },
            };
        }
        if (property.type === 'datetime') {
            assert(isDateTimeValue(value) || value === null, 'Value must be a datetime');
            return {
                kind: 'datetime',
                value: value,
                onChange(value) {
                    props.value.onChange(value);
                },
            };
        }
        if (property.type === 'rating') {
            assert(isRatingValue(value) || value === null, 'Value must be a rating');
            return {
                kind: 'number',
                value: value,
                onChange(value) {
                    console.log('rating not implemented', value);
                },
            };
        }
        if (property.type === 'user') {
            assert(isUserValue(value) || value === null, 'Value must be a user');
            return {
                kind: 'text',
                value: value,
                format: null,
                onChange(value) {
                    console.log('user not implemented', value);
                },
            };
        }
        if (property.type === 'link') {
            assert(isLinkValue(value) || value === null, 'Value must be a link');
            return {
                kind: 'text',
                value: value?.href ?? '',
                format: null,
                onChange(value) {
                    console.log('link not implemented', value);
                },
            };
        }
        if (property.type === 'snippet') {
            assert(isSnippetValue(value) || value === null, 'Value must be a snippet');
            return {
                property,
                kind: 'snippet',
                value: value
                    ? {
                          id: value,
                      }
                    : null,
                onChange(value) {
                    props.value.onChange(value ? value.id : null);
                },
            };
        }
        assertNever(property);
    }

    return {
        useProps(context, item, props): ValueFieldViewProps {
            const optionsByKey = useMemo(
                () =>
                    item.property.type === 'enum'
                        ? keyBy(item.property.config.options, (option) => option.key)
                        : null,
                [item.property]
            );

            const elementProps = getElementContainerProps(
                props,
                { ...item.property, optionByKey: optionsByKey },
                item.value
            );

            return {
                property: {
                    id: item.property.key,
                    label: item.property.display_name,
                },
                tooltip: {
                    label: item.property.display_name,
                },
                element: elementProps,
            };
        },
    };
}
